import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  UploadCloud,
  FileText,
  Download,
  Trash2,
  Crown,
  ArrowRight,
  Loader2,
  CheckCircle2,
  Lock,
} from "lucide-react";

const COUNTRIES = [
  { id: "mx", label: "México" },
  { id: "ar", label: "Argentina" },
  { id: "co", label: "Colombia" },
  { id: "cl", label: "Chile" },
  { id: "pe", label: "Perú" },
  { id: "es", label: "España" },
  { id: "other", label: "Otro país" },
];

const UNIVERSITIES_BY_COUNTRY = {
  mx: [
    "UNAM — Universidad Nacional Autónoma de México",
    "IPN — Instituto Politécnico Nacional",
    "ITESM — Tecnológico de Monterrey",
    "UAM — Universidad Autónoma Metropolitana",
    "UANL — Universidad Autónoma de Nuevo León",
    "BUAP — Benemérita Universidad Autónoma de Puebla",
    "UdG — Universidad de Guadalajara",
    "UDLAP — Universidad de las Américas Puebla",
    "ITAM — Instituto Tecnológico Autónomo de México",
    "UAEM — Universidad Autónoma del Estado de México",
    "Otra universidad mexicana",
  ],
  ar: ["UBA", "UNLP", "UNC", "UTN", "Otra universidad argentina"],
  co: ["Universidad Nacional de Colombia", "Andes", "Javeriana", "Otra universidad colombiana"],
  cl: ["Universidad de Chile", "PUC Chile", "USACH", "Otra universidad chilena"],
  pe: ["UNMSM", "PUCP", "UNI", "Otra universidad peruana"],
  es: ["UCM", "UAM Madrid", "UB", "UNED", "Otra universidad española"],
  other: ["Otra universidad"],
};

const CITATION_FORMATS = ["APA 7", "Vancouver", "Chicago", "MLA", "IEEE", "Otro"];

const PUBLIC_TEMPLATES = [
  { name: "UNAM", facultad: "Filosofía y Letras", formato: "APA 7", color: "bg-forest" },
  { name: "IPN", facultad: "ESIME / ESCA / ESIA", formato: "IEEE", color: "bg-[#A67C52]" },
  { name: "ITESM", facultad: "Tec21", formato: "APA 7", color: "bg-[#4A4A55]" },
  { name: "UAM", facultad: "Xochimilco / Iztapalapa", formato: "Chicago", color: "bg-[#7E9A7C]" },
  { name: "UANL", facultad: "FIME / FCQ", formato: "Vancouver", color: "bg-[#D9C7A7]" },
  { name: "BUAP", facultad: "Medicina / Derecho", formato: "Vancouver", color: "bg-forest" },
];

import ApplyTemplateDialog from "@/components/app/ApplyTemplateDialog";
import { Layers, Sparkles as SparklesIcon } from "lucide-react";

const CATALOG_COVERS = ["bg-forest", "bg-[#A67C52]", "bg-[#7E9A7C]", "bg-[#4A4A55]", "bg-[#D9C7A7]"];

export default function TemplatesView() {
  const { user } = useAuth();
  const isPremium = user?.plan === "premium";
  const [country, setCountry] = useState("mx");
  const [universidad, setUniversidad] = useState("");
  const [format, setFormat] = useState("APA 7");
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  // Curated catalog (Prompt 5)
  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogPickerId, setCatalogPickerId] = useState("");
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [selectedCatalog, setSelectedCatalog] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/templates");
      setTemplates(data.templates || []);
    } catch (e) {
      toast.error("No se pudieron cargar tus plantillas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    (async () => {
      setCatalogLoading(true);
      try {
        const { data } = await api.get("/catalog/templates");
        setCatalog(data.templates || []);
      } catch (err) {
        console.warn("[TemplatesView] catalog fetch failed", err);
      } finally {
        setCatalogLoading(false);
      }
    })();
  }, []);

  const handleFiles = async (files) => {
    const file = files?.[0];
    if (!file) return;
    if (!isPremium) {
      toast.error("Solo disponible en Premium.");
      return;
    }
    if (!universidad) {
      toast.error("Selecciona país y universidad antes de subir.");
      return;
    }
    if (file.type !== "application/pdf") {
      toast.error("Solo se aceptan PDFs.");
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error("Máximo 15 MB.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("country", COUNTRIES.find((c) => c.id === country)?.label || country);
      fd.append("university", universidad);
      fd.append("citation_format", format);
      fd.append("file", file);
      const { data } = await api.post("/templates/upload", fd);
      setTemplates((t) => [data, ...t]);
      toast.success("Reglamento subido. Lo usaremos para personalizar tu editor.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo subir el archivo");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const download = async (t) => {
    try {
      const resp = await api.get(`/templates/${t.template_id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = t.original_filename || "reglamento.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("No se pudo descargar");
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`¿Eliminar reglamento "${t.original_filename}"?`)) return;
    try {
      await api.delete(`/templates/${t.template_id}`);
      setTemplates((arr) => arr.filter((x) => x.template_id !== t.template_id));
      toast.success("Eliminado");
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  return (
    <div className="px-6 lg:px-12 py-8 lg:py-10 max-w-6xl mx-auto" data-testid="templates-view">
      <p className="text-[11px] tracking-[0.22em] uppercase text-copper font-medium">
        Plantillas universitarias
      </p>
      <h1 className="mt-2 font-serif text-3xl sm:text-4xl text-ink leading-tight text-balance">
        Sube el reglamento de tu universidad. Adaptamos el editor.
      </h1>
      <p className="mt-3 text-[14px] text-ink-soft max-w-2xl">
        Acepta PDFs con márgenes, capitulado, formato de citación y portada. Extraemos las reglas y configuramos tu plantilla automáticamente.
      </p>

      {/* Upload zone */}
      <div className="mt-8 rounded-2xl border border-rule bg-white overflow-hidden">
        <div className="px-5 py-4 border-b border-rule flex items-center gap-2 bg-paper-2/40">
          <span className="grid place-items-center h-8 w-8 rounded-full bg-forest text-paper">
            <UploadCloud size={14} />
          </span>
          <p className="font-serif text-lg text-ink">Subir reglamento (PDF)</p>
          {!isPremium && (
            <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-[#FBF6EC] text-copper px-2.5 py-0.5 text-[10px] tracking-[0.18em] uppercase font-medium">
              <Crown size={10} /> Premium
            </span>
          )}
        </div>

        <div className="p-5 grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Selectors */}
          <div className="lg:col-span-1 flex flex-col gap-3">
            <Field label="País">
              <select
                data-testid="tpl-country"
                value={country}
                onChange={(e) => {
                  setCountry(e.target.value);
                  setUniversidad("");
                }}
                className="px-3 py-2 rounded-lg border border-rule bg-white text-[13px] outline-none focus:border-forest"
              >
                {COUNTRIES.map((c) => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Universidad">
              <select
                data-testid="tpl-university"
                value={universidad}
                onChange={(e) => setUniversidad(e.target.value)}
                className="px-3 py-2 rounded-lg border border-rule bg-white text-[13px] outline-none focus:border-forest"
              >
                <option value="">— Selecciona —</option>
                {(UNIVERSITIES_BY_COUNTRY[country] || []).map((u) => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>
            </Field>
            <Field label="Formato de citación">
              <select
                data-testid="tpl-format"
                value={format}
                onChange={(e) => setFormat(e.target.value)}
                className="px-3 py-2 rounded-lg border border-rule bg-white text-[13px] outline-none focus:border-forest"
              >
                {CITATION_FORMATS.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </Field>
          </div>

          {/* Drop zone */}
          <div className="lg:col-span-2">
            <div
              data-testid="tpl-dropzone"
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              onClick={() => isPremium && universidad && inputRef.current?.click()}
              className={`relative h-full min-h-[180px] rounded-xl border-2 border-dashed transition-all cursor-pointer
                ${!isPremium
                  ? "bg-paper-2/40 border-rule text-ink-soft cursor-not-allowed"
                  : dragOver
                  ? "border-forest bg-forest/5"
                  : "border-rule bg-paper-2/30 hover:border-forest/40 hover:bg-white"}`}
            >
              <input
                ref={inputRef}
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={(e) => handleFiles(e.target.files)}
                data-testid="tpl-file-input"
              />
              <div className="absolute inset-0 grid place-items-center p-5">
                {uploading ? (
                  <div className="text-center">
                    <Loader2 size={22} className="animate-spin mx-auto text-forest" />
                    <p className="mt-2 text-[13px] text-ink-soft">Subiendo…</p>
                  </div>
                ) : !isPremium ? (
                  <div className="text-center max-w-sm">
                    <span className="grid place-items-center h-10 w-10 rounded-full bg-forest text-paper mx-auto">
                      <Lock size={15} />
                    </span>
                    <p className="mt-3 font-serif text-lg text-ink">Subida disponible en Premium</p>
                    <p className="mt-1 text-[12.5px] text-ink-soft">
                      Pasa a Premium para subir el PDF de tu universidad y personalizar el editor.
                    </p>
                    <Link
                      to="/pricing"
                      data-testid="tpl-locked-cta"
                      className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[12.5px] font-medium text-paper bg-forest hover:bg-forest-dark"
                    >
                      <Crown size={12} className="text-[#E0C58A]" /> Ver planes
                      <ArrowRight size={12} />
                    </Link>
                  </div>
                ) : !universidad ? (
                  <div className="text-center">
                    <UploadCloud size={28} className="text-ink-soft mx-auto" />
                    <p className="mt-2 font-serif text-lg text-ink">Primero elige tu universidad.</p>
                    <p className="text-[12.5px] text-ink-soft">
                      A la izquierda hay un selector de país y universidad.
                    </p>
                  </div>
                ) : (
                  <div className="text-center">
                    <UploadCloud size={28} className="text-forest mx-auto" />
                    <p className="mt-2 font-serif text-lg text-ink">
                      Arrastra el PDF aquí o haz clic
                    </p>
                    <p className="text-[12.5px] text-ink-soft">
                      Máximo 15 MB · solo .pdf
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* My templates */}
      <section className="mt-10">
        <h2 className="font-serif text-2xl text-ink">Tus reglamentos</h2>
        {loading ? (
          <p className="mt-4 text-ink-soft text-[13px]">Cargando…</p>
        ) : templates.length === 0 ? (
          <p className="mt-4 text-ink-soft text-[13px] italic">
            Aún no has subido reglamentos.
          </p>
        ) : (
          <ul className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="tpl-list">
            {templates.map((t) => (
              <li
                key={t.template_id}
                data-testid={`tpl-item-${t.template_id}`}
                className="flex items-center gap-3 rounded-xl border border-rule bg-white p-3.5"
              >
                <span className="grid place-items-center h-10 w-10 rounded-lg bg-[#FBE9E5] text-[#B6533F] shrink-0">
                  <FileText size={16} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-serif text-[15px] text-ink truncate">{t.university}</p>
                  <p className="text-[11.5px] text-ink-soft truncate">
                    {t.original_filename} · {(t.size / 1024 / 1024).toFixed(2)} MB · {t.citation_format || "Sin formato"}
                  </p>
                </div>
                <button
                  data-testid={`tpl-download-${t.template_id}`}
                  onClick={() => download(t)}
                  aria-label="Descargar"
                  className="h-8 w-8 grid place-items-center rounded-full hover:bg-paper-2 text-ink-soft hover:text-forest"
                >
                  <Download size={14} />
                </button>
                <button
                  data-testid={`tpl-delete-${t.template_id}`}
                  onClick={() => remove(t)}
                  aria-label="Eliminar"
                  className="h-8 w-8 grid place-items-center rounded-full hover:bg-paper-2 text-ink-soft hover:text-[#B6533F]"
                >
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Curated catalog (Prompt 5) */}
      <section className="mt-12" data-testid="catalog-section">
        <div className="flex items-end justify-between gap-2 mb-4 flex-wrap">
          <div>
            <p className="text-[11px] tracking-[0.22em] uppercase text-copper font-medium inline-flex items-center gap-1.5">
              <Layers size={11} /> Plantillas oficiales con formato
            </p>
            <h2 className="font-serif text-2xl text-ink mt-1">
              Aplica un formato curado a tu proyecto en 1 clic
            </h2>
          </div>
          <p className="text-[11.5px] text-ink-soft max-w-xs">
            Márgenes, fuente, capitulado e idioma de citas se inyectan automáticamente en tu editor.
          </p>
        </div>

        {catalogLoading ? (
          <p className="text-[13px] text-ink-soft italic">Cargando catálogo…</p>
        ) : catalog.length === 0 ? (
          <p className="text-[13px] text-ink-soft italic">Catálogo vacío.</p>
        ) : (
          <>
            {/* Dropdown selector (per prompt requirement) */}
            <div className="rounded-2xl border border-rule bg-white p-4 sm:p-5 mb-5 flex flex-col sm:flex-row sm:items-center gap-3">
              <label className="flex flex-col gap-1 flex-1">
                <span className="text-[10px] tracking-[0.22em] uppercase text-ink-soft/80 font-medium">
                  Universidad / formato
                </span>
                <select
                  data-testid="catalog-dropdown"
                  value={catalogPickerId}
                  onChange={(e) => setCatalogPickerId(e.target.value)}
                  className="px-3 py-2.5 rounded-lg border border-rule bg-paper-2/30 focus:bg-white text-[13.5px] outline-none focus:border-forest"
                >
                  <option value="">— Elige tu universidad —</option>
                  {catalog.map((c) => (
                    <option key={c.catalog_id} value={c.catalog_id}>
                      {c.country} · {c.university_name} · {c.citation_style}
                    </option>
                  ))}
                </select>
              </label>
              <button
                data-testid="catalog-apply-from-dropdown"
                disabled={!catalogPickerId}
                onClick={() => {
                  const tpl = catalog.find((c) => c.catalog_id === catalogPickerId);
                  if (tpl) {
                    setSelectedCatalog(tpl);
                    setApplyDialogOpen(true);
                  }
                }}
                className="self-end sm:self-auto inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] font-medium text-paper bg-forest hover:bg-forest-dark disabled:opacity-60 whitespace-nowrap"
              >
                <SparklesIcon size={13} className="text-[#E0C58A]" />
                Aplicar a un proyecto
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="catalog-grid">
              {catalog.map((c, i) => (
                <button
                  key={c.catalog_id}
                  data-testid={`catalog-card-${c.catalog_id}`}
                  onClick={() => {
                    setSelectedCatalog(c);
                    setApplyDialogOpen(true);
                  }}
                  className="text-left rounded-2xl border border-rule bg-white overflow-hidden hover:shadow-[0_20px_40px_-25px_rgba(39,59,44,0.25)] hover:-translate-y-[1px] transition-all flex flex-col"
                >
                  <div className={`${CATALOG_COVERS[i % CATALOG_COVERS.length]} text-paper px-5 py-5`}>
                    <p className="text-[10px] tracking-[0.22em] uppercase font-medium opacity-80">
                      {c.country}
                    </p>
                    <h3 className="mt-1 font-serif text-xl leading-tight line-clamp-2">
                      {c.university_name}
                    </h3>
                  </div>
                  <div className="px-5 py-4 flex-1 flex flex-col gap-2">
                    <div className="flex items-center gap-2 text-[11.5px]">
                      <span className="inline-flex items-center rounded-full bg-paper-2 px-2 py-0.5 text-ink-soft">
                        {c.citation_style}
                      </span>
                      <span className="text-ink-soft">
                        {c.font_family} {c.font_size_pt}pt
                      </span>
                    </div>
                    <p className="text-[11.5px] text-ink-soft">
                      Márgenes {c.margin_top_cm}/{c.margin_bottom_cm}/{c.margin_left_cm}/{c.margin_right_cm} cm
                    </p>
                    <p className="text-[11.5px] text-ink mt-1">
                      {c.required_chapters?.length || 0} capítulos requeridos
                    </p>
                    <span className="mt-auto inline-flex items-center gap-1.5 text-[12.5px] font-medium text-forest">
                      <SparklesIcon size={12} className="text-copper" /> Aplicar plantilla
                      <ArrowRight size={12} />
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </section>

      <ApplyTemplateDialog
        open={applyDialogOpen}
        onOpenChange={setApplyDialogOpen}
        template={selectedCatalog}
      />

      {/* Public catalogue (legacy preview) */}
      <section className="mt-12">
        <div className="flex items-end justify-between gap-2 mb-4">
          <h2 className="font-serif text-2xl text-ink">Catálogo público</h2>
          <p className="text-[11.5px] text-ink-soft">
            Plantillas oficiales preconfiguradas
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {PUBLIC_TEMPLATES.map((u) => (
            <div
              key={u.name}
              className="rounded-2xl border border-rule bg-white overflow-hidden flex flex-col"
            >
              <div className={`${u.color} text-paper px-5 py-4`}>
                <p className="text-[10px] tracking-[0.22em] uppercase font-medium opacity-80">
                  Plantilla oficial
                </p>
                <h3 className="mt-1 font-serif text-2xl leading-tight">{u.name}</h3>
                <p className="mt-1 text-[12px] opacity-80">{u.facultad}</p>
              </div>
              <div className="px-5 py-4 flex items-center justify-between">
                <span className="text-[12px] font-medium text-forest">{u.formato}</span>
                <span className="inline-flex items-center gap-1 text-[11px] text-ink-soft">
                  <CheckCircle2 size={11} className="text-forest" /> Lista para Premium
                </span>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[10px] tracking-[0.22em] uppercase text-ink-soft/80 font-medium">
        {label}
      </span>
      {children}
    </label>
  );
}
